import React, {FC, useState, useEffect} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {getVesselList} from '../../Management/core/_requests'
import {Vessel} from '../../Management/core/_models'
import {filterVesselsForManagementList, isLikelyVesselAssignedUser} from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface UploadCertificate {
  certificateName: string
  dateOfIssue: string
  dateOfExpiry?: string
  fileName: string
  fileSize: string
}

const API_URL = process.env.REACT_APP_API_URL
const OFFICE_CERTIFICATE_API_URL = `${API_URL}/qhse/office-certificates`

const OfficeCertificateUploadModal: FC<{
  isOpen: boolean
  onClose: () => void
  onSubmit: (
    certificate: Omit<UploadCertificate, 'id' | 'uploadedDate' | 'uploadedBy' | 'fileUrl'>
  ) => void
  selectedVesselId?: string
}> = ({isOpen, onClose, onSubmit, selectedVesselId}) => {

  const {auth, currentUser} = useAuth()
  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const userId = (auth?.userDetails as any)?.id
  const username = (auth?.userDetails as any)?.username
  const [formData, setFormData] = useState<{
    certificateCategoryId: string
    certificateNameId: string
    certificateName: string
    dateOfIssue: string
    dateOfExpiry: string
    file: File | null
    vesselId: string
    remark: string
  }>({
    certificateCategoryId: '',
    certificateNameId: '',
    certificateName: '',
    dateOfIssue: '',
    dateOfExpiry: '',
    file: null,
    vesselId: '',
    remark: '',
  })
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [categories, setCategories] = useState<Array<{id: number, name: string}>>([])
  const [certificateNames, setCertificateNames] = useState<Array<{id: number, name: string, categoryId: number}>>([])
  const [filteredCertificateNames, setFilteredCertificateNames] = useState<Array<{id: number, name: string}>>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingVessels, setIsLoadingVessels] = useState(false)
  const [isLoadingLookups, setIsLoadingLookups] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      fetchVessels()
      fetchCertificateLookups()
    }
  }, [isOpen])
  
  useEffect(() => {
    if (isOpen && formData.vesselId) {
      fetchCertificateLookups()
      setFormData(prev => ({...prev, certificateCategoryId: '', certificateNameId: ''}))
      setFilteredCertificateNames([])
    }
  }, [formData.vesselId, isOpen])
  
  useEffect(() => {
    if (formData.certificateCategoryId) {
      const filtered = certificateNames
        .filter(cn => cn.categoryId === Number(formData.certificateCategoryId))
        .map(cn => ({id: cn.id, name: cn.name}))
      setFilteredCertificateNames(filtered)
      setFormData(prev => ({...prev, certificateNameId: ''}))
    } else {
      setFilteredCertificateNames([])
    }
  }, [formData.certificateCategoryId, certificateNames])
  
  const fetchCertificateLookups = async () => {
    setIsLoadingLookups(true)
    try {
      const token = auth?.auth.jwt
      const vesselId = formData.vesselId || selectedVesselId
      
      const namesParams = new URLSearchParams()
      namesParams.append('targetType', 'OFFICE')
      if (vesselId) {
        namesParams.append('vesselId', vesselId)
        namesParams.append('excludeUsed', 'true')
      }
      
      const categoryParams = new URLSearchParams()
      categoryParams.append('targetType', 'OFFICE')
      if (vesselId) {
        categoryParams.append('vesselId', vesselId)
      }

      const [categoriesRes, namesRes] = await Promise.all([
        fetch(`${API_URL}/qhse/certificates/categories?${categoryParams.toString()}`, {
          headers: {Authorization: `Bearer ${token}`}
        }),
        fetch(`${API_URL}/qhse/certificates/names?${namesParams.toString()}`, {
          headers: {Authorization: `Bearer ${token}`}
        })
      ])
      
      if (categoriesRes.ok) {
        const cats = await categoriesRes.json()
        setCategories(cats.map((c: any) => ({id: c.id, name: c.name})))
      }
      
      if (namesRes.ok) {
        const names = await namesRes.json()
        setCertificateNames(names.map((n: any) => ({
          id: n.id,
          name: n.name,
          categoryId: n.categoryId
        })))
      }
    } catch (error) {
      console.error('Failed to fetch certificate lookups:', error)
      toast.error('Failed to load certificate options')
    } finally {
      setIsLoadingLookups(false)
    }
  }

  useEffect(() => {
    if (isOpen && shipboardUser && currentUser?.vessel?.id) {
      setFormData(prev => ({...prev, vesselId: currentUser.vessel!.id.toString()}))
    } else if (isOpen && shipboardUser) {
      toast.error('No vessel assigned to this user profile')
      setFormData(prev => ({...prev, vesselId: ''}))
    }
  }, [isOpen, shipboardUser, currentUser])

  const fetchVessels = async () => {
    if (shipboardUser) {
      setVessels([])
      setIsLoadingVessels(false)
      return
    }

    setIsLoadingVessels(true)
    try {
      const vesselList = await getVesselList()
      setVessels(filterVesselsForManagementList(vesselList, currentUser))
    } catch (error) {
      console.error('Failed to fetch vessel list:', error)
      toast.error('Failed to load vessels')
    } finally {
      setIsLoadingVessels(false)
    }
  }

  const resetForm = () => {
    setFormData({
      certificateCategoryId: '',
      certificateNameId: '',
      certificateName: '',
      dateOfIssue: '',
      dateOfExpiry: '',
      file: null,
      vesselId: '',
      remark: '',
    })
    setFilteredCertificateNames([])
    setErrors({})
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.certificateCategoryId) {
      newErrors.certificateCategoryId = 'Certificate category is required'
    }
    if (!formData.certificateNameId) {
      newErrors.certificateNameId = 'Certificate name is required'
    }
    if (!shipboardUser && !formData.vesselId) {
      newErrors.vesselId = 'Please select a vessel'
    }
    if (!formData.dateOfIssue) {
      newErrors.dateOfIssue = 'Issue date is required'
    }

    if (formData.dateOfExpiry) {
      const expiryDate = new Date(formData.dateOfExpiry)
      const today = new Date()
      if (expiryDate <= today) {
        newErrors.dateOfExpiry = 'Expiry date must be in the future'
      }
      if (formData.dateOfIssue) {
        const issueDate = new Date(formData.dateOfIssue)
        if (issueDate >= expiryDate) {
          newErrors.dateOfExpiry = 'Expiry date must be after issue date'
        }
      }
    }

    if (!formData.file) {
      newErrors.file = 'PDF file is required'
    } else if (formData.file.type !== 'application/pdf') {
      newErrors.file = 'Only PDF files are allowed'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)
  }

  useEffect(() => {
    const uploadCertificate = async () => {
      if (!isSubmitting) return
      if (!formData.file) return
      const token = auth?.auth.jwt

      const form = new FormData()
      if (formData.certificateNameId) {
        form.append('certificateNameId', formData.certificateNameId)
      } else if (formData.certificateName) {
        form.append('certificateName', formData.certificateName)
      }
      form.append('dateOfIssue', formData.dateOfIssue)
      if (formData.dateOfExpiry) {
        form.append('dateOfExpiry', formData.dateOfExpiry)
      }
      form.append('uploadedBy', userId)
      form.append('uploadedByName', username)
      form.append('vesselId', formData.vesselId)
      form.append('remarks', formData.remark)
      form.append('file', formData.file)

      try {
        const response = await fetch(`${OFFICE_CERTIFICATE_API_URL}/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: form,
        })
        if (!response.ok) throw new Error('Upload failed')
        toast.success('Office certificate uploaded successfully!')
        resetForm()
        onClose()
      } catch (error) {
        console.error(error)
        toast.error('Failed to upload office certificate')
      } finally {
        setIsSubmitting(false)
      }
    }

    if (isSubmitting) {
      uploadCertificate()
    }
  }, [isSubmitting])

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
    >
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content'>
          <form onSubmit={handleSubmit}>
            <div className='modal-header'>
              <h5 className='modal-title'>
                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-3 me-3' />
                Upload Office Certificate
              </h5>
              <button
                type='button'
                className='btn-close'
                onClick={handleClose}
                disabled={isSubmitting}
              />
            </div>

            <div className='modal-body'>
              <div className='row'>
                {!shipboardUser && (
                  <div className='col-md-6 mb-6'>
                    <label className='required fw-semibold fs-6 mb-2'>Select Vessel</label>
                    <SearchableSelect
                      className={`form-select form-select-solid ${errors.vesselId ? 'is-invalid' : ''}`}
                      value={formData.vesselId}
                      onChange={(e) => setFormData((prev) => ({...prev, vesselId: e.target.value}))}
                      disabled={isSubmitting || isLoadingVessels || shipboardUser}
                    >
                      <option value=''>
                        {isLoadingVessels ? 'Loading vessels...' : 'Choose a vessel'}
                      </option>
                      {vessels.map((vessel) => (
                        <option key={vessel.id} value={vessel.id}>
                          {(vessel as any).fleet_name}
                        </option>
                      ))}
                    </SearchableSelect>
                    {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
                  </div>
                )}

                <div className='col-md-6 mb-6'>
                  <label className='required fw-semibold fs-6 mb-2'>Certificate Category</label>
                  <SearchableSelect
                    className={`form-select form-select-solid ${
                      errors.certificateCategoryId ? 'is-invalid' : ''
                    }`}
                    value={formData.certificateCategoryId}
                    onChange={(e) =>
                      setFormData((prev) => ({...prev, certificateCategoryId: e.target.value, certificateNameId: ''}))
                    }
                    disabled={isSubmitting || isLoadingLookups}
                  >
                    <option value=''>
                      {isLoadingLookups ? 'Loading categories...' : 'Select Category'}
                    </option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.certificateCategoryId && (
                    <div className='invalid-feedback'>{errors.certificateCategoryId}</div>
                  )}
                </div>

                <div className='col-md-6 mb-6'>
                  <label className='required fw-semibold fs-6 mb-2'>Certificate Name</label>
                  <SearchableSelect
                    className={`form-select form-select-solid ${
                      errors.certificateNameId ? 'is-invalid' : ''
                    }`}
                    value={formData.certificateNameId}
                    onChange={(e) =>
                      setFormData((prev) => ({...prev, certificateNameId: e.target.value}))
                    }
                    disabled={isSubmitting || isLoadingLookups || !formData.certificateCategoryId}
                  >
                    <option value=''>
                      {!formData.certificateCategoryId 
                        ? 'Select category first'
                        : isLoadingLookups 
                        ? 'Loading certificates...' 
                        : 'Select Certificate Name'}
                    </option>
                    {filteredCertificateNames.map((certName) => (
                      <option key={certName.id} value={certName.id}>
                        {certName.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.certificateNameId && (
                    <div className='invalid-feedback'>{errors.certificateNameId}</div>
                  )}
                </div>

                <div className='col-md-6 mb-6'>
                  <label className='required fw-semibold fs-6 mb-2'>Date of Issue</label>
                  <input
                    type='date'
                    className={`form-control form-control-solid ${
                      errors.dateOfIssue ? 'is-invalid' : ''
                    }`}
                    value={formData.dateOfIssue}
                    onChange={(e) => setFormData((prev) => ({...prev, dateOfIssue: e.target.value}))}
                    disabled={isSubmitting}
                  />
                  {errors.dateOfIssue && (
                    <div className='invalid-feedback'>{errors.dateOfIssue}</div>
                  )}
                </div>

                <div className='col-md-6 mb-6'>
                  <label className='fw-semibold fs-6 mb-2'>Date of Expiry</label>
                  <input
                    type='date'
                    className={`form-control form-control-solid ${errors.dateOfExpiry ? 'is-invalid' : ''}`}
                    value={formData.dateOfExpiry}
                    onChange={(e) => setFormData((prev) => ({...prev, dateOfExpiry: e.target.value}))}
                    disabled={isSubmitting}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  {errors.dateOfExpiry && <div className='invalid-feedback'>{errors.dateOfExpiry}</div>}
                </div>

                <div className='col-12 mb-6'>
                  <label className='fw-semibold fs-6 mb-2'>Remark</label>
                  <textarea
                    className='form-control form-control-solid'
                    placeholder='Enter any remarks or notes (optional)'
                    rows={3}
                    value={formData.remark}
                    onChange={(e) =>
                      setFormData((prev) => ({...prev, remark: e.target.value}))
                    }
                    disabled={isSubmitting}
                  />
                </div>

                <div className='col-12 mb-6'>
                  <label className='required fw-semibold fs-6 mb-2'>
                    Certificate File (PDF only)
                  </label>
                  <input
                    type='file'
                    className={`form-control form-control-solid ${errors.file ? 'is-invalid' : ''}`}
                    accept='.pdf'
                    onChange={(e) =>
                      setFormData((prev) => ({...prev, file: e.target.files?.[0] || null}))
                    }
                    disabled={isSubmitting}
                  />
                  {errors.file && <div className='invalid-feedback'>{errors.file}</div>}
                  {formData.file && (
                    <div className='form-text text-muted mt-2'>
                      Selected: {formData.file.name} (
                      {(formData.file.size / (1024 * 1024)).toFixed(1)} MB)
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='modal-footer'>
              <button
                type='button'
                className='btn btn-light'
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    Uploading...
                  </>
                ) : (
                  <>
                    <KTSVG
                      path='/media/icons/duotune/arrows/arr075.svg'
                      className='svg-icon-4 me-2'
                    />
                    Upload Office Certificate
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export {OfficeCertificateUploadModal}
